"use client";

import { useState, useEffect, useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import CartContext from "../../../context/CartContext/CartContext";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Badge,
  List,
  ListItem,
  ListItemText,
  TextField,
  InputAdornment,
  Avatar,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ShoppingCart,
  Receipt,
  ClearAll,
  Search,
  PersonAdd,
  Close,
  Person,
  Add,
  Edit,
  Check,
  Cancel,
  AttachMoney,
  Warning,
  PaymentOutlined,
  ArrowForward,
  People,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import {
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonText,
  IonToolbar,
} from "@ionic/react";
import { removeOutline, add as addIcon } from "ionicons/icons";
import {
  getBarInventoryByBranch,
  getCategories,
} from "../../../api/products/product";
import { createCustomerOrder } from "../../../api/orders/orders";
import { formatCurrencyN } from "../../../common/utils_t";
import {
  createCustomer,
  getCustomerGrades,
  searchCustomers,
} from "../../../api/customers/customers";

const StyledProductCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  cursor: "pointer",
  transition: "transform 0.2s",
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: theme.shadows[4],
  },
}));

// Mock data for waiters/stewards - This would come from an API endpoint
const mockWaiters = [
  { user_id: 1, firstName: "John", middleName: "Michael", lastName: "Doe", role: "waiter" },
  { user_id: 2, firstName: "Sarah", middleName: "Jane", lastName: "Smith", role: "steward" },
  { user_id: 3, firstName: "Michael", middleName: "James", lastName: "Brown", role: "waiter" },
  { user_id: 4, firstName: "Emily", middleName: "Rose", lastName: "Johnson", role: "steward" },
  { user_id: 5, firstName: "David", middleName: "William", lastName: "Wilson", role: "waiter" },
  { user_id: 6, firstName: "Jennifer", middleName: "Marie", lastName: "Davis", role: "steward" },
];

// API function to fetch waiters/stewards (mocked for now)
const getWaiters = async () => {
  // In real implementation, this would be an API call like:
  // return await axios.get('/api/waiters');
  
  // Mock implementation
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        data: mockWaiters,
        status: true,
        message: "Waiters fetched successfully"
      });
    }, 500);
  });
};

const PosPage_bar = () => {
  const history = useHistory();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const {
    items,
    totalAmount,
    addItem,
    removeItem,
    clearCart,
    updateItemPrice,
  } = useContext(CartContext);
  
  // Waiter/Steward states
  const [waiters, setWaiters] = useState([]);
  const [selectedWaiter, setSelectedWaiter] = useState(null);
  const [loadingWaiters, setLoadingWaiters] = useState(false);
  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchedCustomers, setSearchedCustomers] = useState([]);
  const [customerGrades, setCustomerGrades] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const [newCustomer, setNewCustomer] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    phone: "",
    email: "",
    grade: "A",
  });
  const [loading, setLoading] = useState({
    customers: false,
    grades: false,
    products: false,
  });
  const [processingOrder, setProcessingOrder] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [useGradePricing, setUseGradePricing] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editPrice, setEditPrice] = useState("");
  const [editPriceError, setEditPriceError] = useState("");

  const [showProcessPaymentDialog, setShowProcessPaymentDialog] = useState(false);
  const [lastOrderCustomer, setLastOrderCustomer] = useState(null);

  // Helper function to get available quantity for a product
  const getAvailableQuantity = (productId) => {
    const product = allProducts.find(p => p.product_id === productId);
    return product ? product.qty : 0;
  };

  // Helper function to get current cart quantity for a product
  const getCartQuantity = (productId) => {
    const cartItem = items.find(item => item.product_id === productId);
    return cartItem ? cartItem.quantity : 0;
  };

  // Helper function to get remaining available quantity for a product
  const getRemainingQuantity = (productId) => {
    const availableQty = getAvailableQuantity(productId);
    const cartQty = getCartQuantity(productId);
    return Math.max(0, availableQty - cartQty);
  };

  const filteredProducts = useMemo(() => {
    if (!Array.isArray(products)) {
      return [];
    }
    return products.filter((product) => {
      const matchesCategory = selectedCategory
        ? String(product.productCategory) === String(selectedCategory)
        : true;
      const matchesSearch = product.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [products, selectedCategory, searchQuery]);

  const validatePrice = (price, minSalePrice) => {
    const numPrice = Number.parseFloat(price);
    const numMinPrice = Number.parseFloat(minSalePrice);
    if (isNaN(numPrice) || numPrice <= 0) {
      return "Please enter a valid price";
    }
    if (numPrice < numMinPrice) {
      return `Price cannot be less than minimum sale price of ${formatCurrencyN(
        numMinPrice
      )}`;
    }
    return "";
  };

  const handleAddToCart = (product) => {
    // Check available quantity
    const availableQty = getRemainingQuantity(product.product_id);
    if (availableQty <= 0) {
      setSnackbarMessage(`Cannot add ${product.name}. Only ${getAvailableQuantity(product.product_id)} available in stock.`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    
    const priceToUse = useGradePricing && selectedCustomer 
      ? product.price - (selectedCustomer?.grade_value || 0) * product.price
      : product.price;
    
    addItem({
      id: product.product_id,
      product_name: product.name,
      quantity: 1,
      price: priceToUse,
      product_id: product.product_id,
      image: product.img_src,
      base_price: product.price,
      min_sale_price: product.minSalePrice,
      available_qty: product.qty,
    });
  };

  const handleEditItemPrice = (itemId, currentPrice, minSalePrice) => {
    setEditingItemId(itemId);
    setEditPrice(currentPrice.toString());
    setEditPriceError("");
  };

  const handleEditPriceChange = (value, minSalePrice) => {
    setEditPrice(value);
    const error = validatePrice(value, minSalePrice);
    setEditPriceError(error);
  };

  const handleSaveEditPrice = (itemId, minSalePrice) => {
    const error = validatePrice(editPrice, minSalePrice);
    if (error) {
      setEditPriceError(error);
      return;
    }
    if (editPrice && updateItemPrice) {
      updateItemPrice(itemId, Number.parseFloat(editPrice));
      setSnackbarMessage("Price updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    }
    setEditingItemId(null);
    setEditPrice("");
    setEditPriceError("");
  };

  const handleCancelEditPrice = () => {
    setEditingItemId(null);
    setEditPrice("");
    setEditPriceError("");
  };

  const fetchInitialData = async () => {
    try {
      setLoading((prev) => ({ ...prev, products: true, grades: true }));
      const [productsRes, categoriesRes, gradesRes] = await Promise.all([
         getBarInventoryByBranch(),
        getCategories(),
        getCustomerGrades(),
      ]);

      const productsWithCategoryName = productsRes.data.map(p => {
        const category = categoriesRes.data.find(c => c.category_id === p.productCategory);
        return { ...p, category: category ? category.name : 'Unknown' };
      });

      setAllProducts(productsWithCategoryName);
      setProducts(useGradePricing ? [] : productsWithCategoryName);
      setCategories(categoriesRes.data);
      setCustomerGrades(gradesRes.data);
      if (gradesRes.data.length > 0) {
        setNewCustomer((prev) => ({
          ...prev,
          grade: gradesRes.data[0].grade_id,
        }));
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      setSnackbarMessage("Failed to load initial data.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading((prev) => ({ ...prev, products: false, grades: false }));
    }
  };

  const fetchWaiters = async () => {
    try {
      setLoadingWaiters(true);
      const response = await getWaiters();
      if (response.status === true) {
        setWaiters(response.data);
      } else {
        setSnackbarMessage("Failed to load waiters/stewards.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error fetching waiters:", error);
      setSnackbarMessage("Failed to load waiters/stewards.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoadingWaiters(false);
    }
  };

  const fetchCustomers = async (searchTerm) => {
     if (searchTerm.length < 3) {
      setSearchedCustomers([]);
      return;
    }
    try {
      setLoading((prev) => ({ ...prev, customers: true }));
      const response = await searchCustomers(searchTerm);
      setSearchedCustomers(response.data);
    } catch (error) {
      console.error("Error searching customers:", error);
      setSnackbarMessage("Failed to search customers.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading((prev) => ({ ...prev, customers: false }));
    }
  };

  useEffect(() => {
    fetchInitialData();
    fetchWaiters();
  }, []);

  useEffect(() => {
    if (useGradePricing) {
      if (!selectedCustomer || !selectedCustomer.customer_grade) {
        setProducts([]);
        return;
      }
      const gradeId = selectedCustomer.customer_grade;
      const filtered = allProducts.filter(
        (product) =>
          product.grade_ids?.includes(gradeId) ||
          product.grade_id === gradeId ||
          !product.grade_id
      );
      setProducts(filtered);
    } else {
      setProducts(allProducts);
    }
  }, [selectedCustomer, allProducts, useGradePricing]);

  useEffect(() => {
      if (customerSearchQuery.trim() === '') {
        setSearchedCustomers([]);
        return;
      }
      const timerId = setTimeout(() => {
        fetchCustomers(customerSearchQuery);
      }, 500);
      return () => clearTimeout(timerId);
    }, [customerSearchQuery]);

  const onHandleCreateOrder = async () => {
    if (!selectedWaiter) {
      setSnackbarMessage("Please select a waiter/steward before processing order.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!selectedCustomer) {
      setSnackbarMessage("Please select a customer before processing order.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setProcessingOrder(true);
    
    const saleData = {
      customer_id: selectedCustomer?.user_id || null,
      items: items,
      totalPrice: totalAmount,
      paymentType: "cash",
      amountPaid: totalAmount,
      changeGiven: 0,
      pricing_mode: useGradePricing ? "grade" : "manual",
      waiter_id: selectedWaiter?.user_id || null,
      waiter_name: `${selectedWaiter?.firstName} ${selectedWaiter?.lastName}`,
    };

    try {
      const res = await createCustomerOrder(saleData);

      if (res.status === true) {
        setSnackbarMessage("Order created successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setLastOrderCustomer(selectedCustomer);
        setShowProcessPaymentDialog(true);
        clearCart();
        fetchInitialData();
        setSelectedCustomer(null);
      } else {
        setSnackbarMessage("Failed to create order." + (res.message || ""));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
    } catch (error) {
      console.error("Error creating order:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to create order. Please try again.";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setProcessingOrder(false);
    }
  };

  const handleAddCustomer = async () => {
    if (!selectedWaiter) {
      setSnackbarMessage("Please select a waiter/steward before adding a customer.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setLoadingx(true);
    try {
      const response = await createCustomer(newCustomer);
      setSelectedCustomer(response.data);
      setShowCustomerDialog(false);
      setNewCustomer({
        firstName: "",
        middleName: "",
        lastName: "",
        phone: "",
        email: "",
        grade: customerGrades[0]?.grade_id || "",
      });
      setCustomerSearchQuery("");
      setSnackbarMessage("Customer added successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setLoadingx(false);
    } catch (error) {
      setLoadingx(false);
      console.error("Error creating customer:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to add customer. Please check inputs.";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const cartItemRemoveHandler = (id) => {
    removeItem(id);
  };

  const cartItemAddHandler = (item) => {
    // Check if we can add more of this item
    const availableQty = getRemainingQuantity(item.product_id);
    if (availableQty <= 0) {
      setSnackbarMessage(`Cannot add more ${item.product_name}. Only ${getAvailableQuantity(item.product_id)} available in stock.`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    
    addItem({ ...item, quantity: 1 });
  };

  const getProductPrice = (product) => {
    if (useGradePricing && selectedCustomer) {
      return product.price - selectedCustomer?.grade_value * product?.price;
    }
    return product?.price;
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const getFullName = (person) => {
    return `${person.firstName} ${person.middleName ? person.middleName + ' ' : ''}${person.lastName}`;
  };

  const [loadingx, setLoadingx] = useState(false);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>{/* <IonTitle>Pos</IonTitle> */}</IonToolbar>
      </IonHeader>
      <IonContent>
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            bgcolor: "#f5f7fa",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              bgcolor: "#667eea",
              color: "white",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Sales Posting
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {selectedWaiter && (
                  <Chip
                    label={`Waiter: ${getFullName(selectedWaiter)}`}
                    onDelete={() => {
                      setSelectedWaiter(null);
                      setSelectedCustomer(null);
                      clearCart();
                    }}
                    deleteIcon={<Close />}
                    avatar={
                      <Avatar sx={{ bgcolor: "white", color: "#667eea" }}>
                        <People />
                      </Avatar>
                    }
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      "& .MuiChip-deleteIcon": { color: "white" },
                    }}
                  />
                )}
                {selectedCustomer ? (
                  <Chip
                    label={`Customer: ${selectedCustomer.firstName} ${selectedCustomer.lastName} (${selectedCustomer.phone})`}
                    onDelete={() => {
                      setSelectedCustomer(null);
                      clearCart();
                    }}
                    deleteIcon={<Close />}
                    avatar={
                      <Avatar sx={{ bgcolor: "white", color: "#667eea" }}>
                        <Person />
                      </Avatar>
                    }
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      "& .MuiChip-deleteIcon": { color: "white" },
                    }}
                  />
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<PersonAdd />}
                    onClick={() => setShowCustomerDialog(true)}
                    sx={{
                      bgcolor: "white",
                      color: "#667eea",
                      "&:hover": { bgcolor: "#f0f0f0" },
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Select Customer
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          {/* Main Content */}
          <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
            {/* Product Selection Panel */}
            <Box
              sx={{
                width: { xs: "100%", md: "70%" },
                p: 2,
                overflowY: "auto",
                borderRight: { md: "1px solid #e0e0e0" },
              }}
            >
              {useGradePricing && !selectedCustomer ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    textAlign: "center",
                    py: 4,
                  }}
                >
                  <Person
                    sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
                  />
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Please select a customer to view grade-based products
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PersonAdd />}
                    onClick={() => setShowCustomerDialog(true)}
                    sx={{
                      mt: 2,
                      bgcolor: "#667eea",
                      color: "white",
                      "&:hover": { bgcolor: "#5a6fd8" },
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Select Customer
                  </Button>
                </Box>
              ) : (
                <>
                  {/* Search and Categories */}
                  <Box
                    sx={{
                      mb: 2,
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <TextField
                      fullWidth
                      placeholder="Search products..."
                      variant="outlined"
                      size="small"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ flexGrow: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useGradePricing}
                          onChange={(e) => setUseGradePricing(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Grade Pricing"
                    />
                    {!useGradePricing && (
                      <Chip
                        icon={<PaymentOutlined />}
                        label="Manual Pricing"
                        color="secondary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Tabs
                    value={selectedCategory}
                    onChange={(e, newValue) => {
                      setSelectedCategory(newValue);
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
                  >
                    <Tab label="All" value={null} />
                    {categories?.map((category, i) => (
                      <Tab
                        key={i}
                        label={category.name}
                        value={category.category_id}
                      />
                    ))}
                  </Tabs>
                  {/* Product Grid */}
                  {loading.products ? (
                    <Grid container spacing={2}>
                      {Array.from({ length: 8 }).map((_, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <StyledProductCard
                            sx={{
                              height: 180,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <CircularProgress size={40} sx={{ mb: 2 }} />
                            <Typography variant="subtitle1">
                              Loading...
                            </Typography>
                          </StyledProductCard>
                        </Grid>
                      ))}
                    </Grid>
                  ) : filteredProducts?.length > 0 ? (
                    <Grid container spacing={2}>
                      {filteredProducts.map((product, i) => (
                        <Grid item xs={6} sm={4} md={3} key={i}>
                          <StyledProductCard
                            onClick={() => {
                              if (product.qty > 0 && selectedWaiter && selectedCustomer) {
                                handleAddToCart(product);
                              }
                            }}
                            sx={{
                              opacity: (!selectedWaiter || !selectedCustomer) ? 0.5 : 1,
                              cursor: (!selectedWaiter || !selectedCustomer) ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <Avatar
                                src={
                                  product.img_src ||
                                  "/placeholder.svg?height=80&width=80&query=product"
                                }
                                alt={product.name}
                                sx={{ width: 80, height: 80, mb: 1 }}
                              />
                              <Typography
                                variant="subtitle1"
                                align="center"
                                sx={{ fontWeight: 600 }}
                              >
                                {product.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {useGradePricing
                                  ? formatCurrencyN(getProductPrice(product))
                                  : `Base: ${formatCurrencyN(product.price)}`}
                              </Typography>
                              {!useGradePricing && product.minSalePrice && (
                                <Typography
                                  variant="caption"
                                  color="warning.main"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Warning fontSize="small" />
                                  Min:{" "}
                                  {formatCurrencyN(
                                    Number(product.minSalePrice)
                                  )}
                                </Typography>
                              )}
                              <Chip
                                label={
                                  product.qty < 1
                                    ? "Out of Stock"
                                    : `Qty: ${product.qty}`
                                }
                                size="small"
                                color={product.qty < 1 ? "error" : "secondary"}
                                sx={{ mt: 1 }}
                              />
                            </Box>
                          </StyledProductCard>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Grid item xs={12} sx={{ textAlign: "center", p: 4 }}>
                      <Typography>No products found</Typography>
                    </Grid>
                  )}
                </>
              )}
            </Box>

            {/* Cart Panel */}
            <Box
              sx={{
                width: { xs: "100%", md: "30%" },
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.paper",
                boxShadow: { xs: "0 -4px 12px rgba(0,0,0,0.1)", md: "none" },
              }}
            >
              <Box
                sx={{
                  p: 2,
                  bgcolor: "background.paper",
                  borderBottom: "1px solid #e0e0e0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  <Badge
                    badgeContent={items.length}
                    color="primary"
                    sx={{ mr: 1 }}
                  >
                    <ShoppingCart />
                  </Badge>
                  Current Order
                </Typography>
                <Button
                  size="small"
                  color="error"
                  startIcon={<ClearAll />}
                  onClick={clearCart}
                  disabled={items.length === 0}
                  sx={{ borderRadius: 2, textTransform: "none" }}
                >
                  Clear
                </Button>
              </Box>
              <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                {items.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      textAlign: "center",
                      py: 4,
                    }}
                  >
                    <ShoppingCart
                      sx={{ fontSize: 64, color: "text.disabled", mb: 1 }}
                    />
                    <Typography variant="body1" color="text.secondary">
                      Your cart is empty
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select products to add to order
                    </Typography>
                  </Box>
                ) : (
                  <List dense>
                    {items.map((item, i) => (
                      <ListItem
                        key={i}
                        sx={{
                          py: 1,
                          flexDirection: "column",
                          alignItems: "stretch",
                          borderBottom: "1px dashed #eee",
                          "&:last-child": { borderBottom: "none" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Avatar
                            src={
                              item?.image ||
                              "/placeholder.svg?height=40&width=40&query=product"
                            }
                            alt={item.product_name}
                            sx={{ width: 40, height: 40, mr: 2 }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600 }}
                            >
                              {item.product_name}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {editingItemId === item.product_id ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <TextField
                                      size="small"
                                      value={editPrice}
                                      onChange={(e) =>
                                        handleEditPriceChange(
                                          e.target.value,
                                          item.min_sale_price
                                        )
                                      }
                                      type="number"
                                      sx={{ width: 120 }}
                                      error={!!editPriceError}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            ₦
                                          </InputAdornment>
                                        ),
                                      }}
                                      placeholder="Enter new price"
                                    />
                                    <Tooltip title="Accept Price">
                                      <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() =>
                                          handleSaveEditPrice(
                                            item.product_id,
                                            item.min_sale_price
                                          )
                                        }
                                        disabled={!!editPriceError}
                                      >
                                        <Check fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Cancel">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={handleCancelEditPrice}
                                      >
                                        <Cancel fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                  {editPriceError && (
                                    <Typography
                                      variant="caption"
                                      color="error"
                                      sx={{ fontSize: "0.7rem" }}
                                    >
                                      {editPriceError}
                                    </Typography>
                                  )}
                                  {item.min_sale_price && (
                                    <Typography
                                      variant="caption"
                                      color="warning.main"
                                      sx={{ fontSize: "0.7rem" }}
                                    >
                                      Minimum: {formatCurrencyN(item.min_sale_price)}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {formatCurrencyN(item.price)}
                                  </Typography>
                                  {!useGradePricing && (
                                    <Tooltip title="Edit price">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleEditItemPrice(
                                            item.product_id,
                                            item.price,
                                            item.min_sale_price
                                          )
                                        }
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              )}
                            </Box>
                            {item.custom_price && (
                              <Chip
                                label="Custom Price"
                                size="small"
                                color="secondary"
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <IonButton
                              fill="clear"
                              size="small"
                              onClick={() =>
                                cartItemRemoveHandler(item.product_id)
                              }
                            >
                              <IonIcon slot="icon-only" icon={removeOutline} />
                            </IonButton>
                            <IonText>{item.quantity}</IonText>
                            <IonButton
                              fill="clear"
                              size="small"
                              onClick={() => cartItemAddHandler(item)}
                              disabled={getRemainingQuantity(item.product_id) <= 0}
                            >
                              <IonIcon slot="icon-only" icon={addIcon} />
                            </IonButton>
                          </Box>
                        </Box>
                        {/* Show available quantity warning */}
                        {getRemainingQuantity(item.product_id) <= 0 && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                            Maximum quantity reached
                          </Typography>
                        )}
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
              {/* Order Summary */}
              <Box
                sx={{
                  p: 2,
                  borderTop: "1px solid #e0e0e0",
                  bgcolor: "background.paper",
                  boxShadow: "0 -2px 8px rgba(0,0,0,0.05)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Total:
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: "#667eea" }}
                  >
                    {formatCurrencyN(totalAmount)}
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={onHandleCreateOrder}
                  startIcon={
                    processingOrder ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Receipt />
                    )
                  }
                  sx={{
                    mt: 2,
                    bgcolor: "#667eea",
                    color: "white",
                    "&:hover": { bgcolor: "#5a6fd8" },
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    py: 1.5,
                  }}
                  disabled={
                    items.length === 0 ||
                    processingOrder ||
                    selectedCustomer === null ||
                    selectedWaiter === null
                  }
                >
                  {processingOrder ? "Processing..." : "Process Payment"}
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Customer Selection Dialog - WITH WAITER SELECTION AT THE TOP */}
          <Dialog
            open={showCustomerDialog}
            onClose={() => setShowCustomerDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ pb: 1 }}>
              {selectedCustomer ? "Change Customer" : "Select Customer"}
              <IconButton
                sx={{ position: "absolute", right: 8, top: 8 }}
                onClick={() => setShowCustomerDialog(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {/* Waiter/Steward Selection Dropdown - FIRST THING IN THE DIALOG */}
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Select Waiter/Steward</InputLabel>
                  <Select
                    value={selectedWaiter?.user_id || ""}
                    label="Select Waiter/Steward"
                    onChange={(e) => {
                      const waiterId = e.target.value;
                      const selected = waiters.find(w => w.user_id === waiterId);
                      setSelectedWaiter(selected || null);
                    }}
                    disabled={loadingWaiters}
                  >
                    <MenuItem value="">
                      <em>Select a waiter/steward</em>
                    </MenuItem>
                    {waiters.map((waiter) => (
                      <MenuItem key={waiter.user_id} value={waiter.user_id}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: '#667eea' }}>
                            <People fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body1">
                              {getFullName(waiter)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {waiter.role === 'waiter' ? 'Waiter' : 'Steward'}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {loadingWaiters && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="caption">Loading waiters...</Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Customer Search Section */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Search customers by name or phone..."
                  variant="outlined"
                  size="small"
                  value={customerSearchQuery}
                  onChange={(e) => setCustomerSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              {loading.customers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : searchedCustomers.length > 0 ? (
                <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {searchedCustomers?.map(customer => (
                    <ListItem
                      button
                      key={customer.user_id}
                      onClick={() => {
                        if (!selectedWaiter) {
                          setSnackbarMessage("Please select a waiter/steward first.");
                          setSnackbarSeverity("error");
                          setSnackbarOpen(true);
                          return;
                        }
                        setSelectedCustomer(customer);
                        setShowCustomerDialog(false);
                        setCustomerSearchQuery('');
                      }}
                      selected={selectedCustomer?.id === customer.id}
                      disabled={!selectedWaiter}
                    >
                      <ListItemText  
                        primary={`${customer.firstName} ${customer.lastName}`}
                        secondary={`${customer.phone} | ${customer.email || 'No email'}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : customerSearchQuery ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography>No customers found matching "{customerSearchQuery}"</Typography>
                </Box>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography>Enter a name or phone number to search for customers</Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              {/* Add New Customer Section */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Add New Customer
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={newCustomer.firstName}
                    onChange={(e) =>
                      setNewCustomer({
                        ...newCustomer,
                        firstName: e.target.value,
                      })
                    }
                    required
                    disabled={!selectedWaiter}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={newCustomer.lastName}
                    onChange={(e) =>
                      setNewCustomer({
                        ...newCustomer,
                        lastName: e.target.value,
                      })
                    }
                    required
                    disabled={!selectedWaiter}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={newCustomer.phone}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, phone: e.target.value })
                    }
                    required
                    disabled={!selectedWaiter}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={newCustomer.email}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, email: e.target.value })
                    }
                    type="email"
                    disabled={!selectedWaiter}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button
                onClick={() => setShowCustomerDialog(false)}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAddCustomer}
                disabled={
                  loadingx || !newCustomer.firstName || !newCustomer.phone || !selectedWaiter
                }
                startIcon={!loadingx && <Add />}
                sx={{
                  bgcolor: "#667eea",
                  color: "white",
                  "&:hover": { bgcolor: "#5a6fd8" },
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {loadingx ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} color="inherit" />
                    <span>Adding...</span>
                  </Box>
                ) : (
                  "Add Customer"
                )}
              </Button>
            </DialogActions>
          </Dialog>
          
          <Dialog open={showProcessPaymentDialog} onClose={() => setShowProcessPaymentDialog(false)}>
            <DialogTitle>Process Payment</DialogTitle>
            <DialogContent>
                <Typography>Do you want to process payment for this customer?</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowProcessPaymentDialog(false)}>No</Button>
                <Button 
                    onClick={() => {
                        history.push(`/folder/raise-receipt/AddReceipt`, { customer: lastOrderCustomer });
                        setShowProcessPaymentDialog(false);
                    }}
                    startIcon={<ArrowForward />}
                >
                    Yes, Proceed
                </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={(event) => handleSnackbarClose(event, undefined)}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default PosPage_bar;